import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Chip,
  Stack,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import { FolderShared, Description } from '@mui/icons-material';
import { investigationAPI, praAPI } from '../services/api';

const SharedWithMe = () => {
  const navigate = useNavigate();
  const [investigations, setInvestigations] = useState([]);
  const [sharedRequests, setSharedRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [invR, reqR] = await Promise.all([
          investigationAPI.list(true),
          praAPI.getRequests(null, false),
        ]);
        setInvestigations(invR.data);
        // Visibility filtering already happens on the server, so anything returned here
        // that isn't owned by the current user is "shared with me".
        const myId = JSON.parse(localStorage.getItem('user') || '{}').id;
        setSharedRequests(reqR.data.filter((r) => r.user_id !== myId));
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>
      <Typography variant="h4" sx={{ fontFamily: 'Georgia, serif', fontWeight: 700, color: '#00075e', mb: 0.5 }}>
        Shared with me
      </Typography>
      <Typography sx={{ color: '#838996', mb: 3 }}>
        Investigations shared with you, plus requests visible through your organization.
      </Typography>

      <Box sx={{ mb: 4 }}>
        <Typography sx={{ fontFamily: 'Georgia, serif', fontWeight: 700, color: '#00075e', mb: 1.5 }}>
          Investigations
        </Typography>
        {loading ? (
          <Typography sx={{ color: '#838996' }}>Loading…</Typography>
        ) : investigations.length === 0 ? (
          <Typography sx={{ color: '#838996' }}>Nothing shared with you yet.</Typography>
        ) : (
          <Stack direction="row" spacing={1.25} sx={{ flexWrap: 'wrap', gap: 1.25 }}>
            {investigations.map((inv) => (
              <Paper
                key={inv.id}
                onClick={() => navigate(`/investigations/${inv.id}`)}
                elevation={0}
                sx={{
                  border: '1px solid #e0ddd6',
                  borderRadius: 2,
                  px: 2,
                  py: 1.25,
                  cursor: 'pointer',
                  minWidth: 220,
                  '&:hover': { borderColor: '#2a52be', boxShadow: '0 2px 8px rgba(42,82,190,0.1)' },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FolderShared sx={{ color: '#2a52be', fontSize: 18 }} />
                  <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#00075e' }}>
                    {inv.name}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: '0.72rem', color: '#838996', mt: 0.25 }}>
                  {inv.request_count} request{inv.request_count !== 1 ? 's' : ''} · {inv.my_permission}
                </Typography>
                {inv.description && (
                  <Typography sx={{ fontSize: '0.78rem', color: '#6a6f7a', mt: 0.5 }}>
                    {inv.description}
                  </Typography>
                )}
              </Paper>
            ))}
          </Stack>
        )}
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box>
        <Typography sx={{ fontFamily: 'Georgia, serif', fontWeight: 700, color: '#00075e', mb: 1.5 }}>
          Shared Requests
        </Typography>
        {sharedRequests.length === 0 ? (
          <Typography sx={{ color: '#838996' }}>No shared requests.</Typography>
        ) : (
          <List sx={{ border: '1px solid #e0ddd6', borderRadius: 2, p: 0 }}>
            {sharedRequests.map((r, i) => (
              <React.Fragment key={r.id}>
                <ListItem disablePadding>
                  <ListItemButton onClick={() => navigate(`/requests/${r.id}`)}>
                    <Description sx={{ color: '#2a52be', mr: 1.5, fontSize: 18 }} />
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <span>{r.subject || r.natural_language_input?.substring(0, 80) || `Request #${r.id}`}</span>
                          <Chip size="small" label={r.status} />
                        </Box>
                      }
                      secondary={`From ${r.owner_username || 'another user'} · ${r.destination || '—'}`}
                    />
                  </ListItemButton>
                </ListItem>
                {i < sharedRequests.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Box>
    </Container>
  );
};

export default SharedWithMe;
